import { useState } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { Card } from "../../theme/components/index.js";
import { colorsSemantic } from "../../theme/tokens/colors-semantic.js";
import {
  barHeightPct,
  formatDe,
  formatDeltaPct,
  monthLabelVisible,
  seriesMax,
} from "../../utils/dashboardFormat.js";

const BAR_AREA_HEIGHT = 38;
const DEFAULT_HINT = "Letzte 12 Monate · Balken für Details";

/**
 * TrendCard — the click-dummy trend tile: title, the newest month's value as the
 * headline, a month-over-month delta pill, and a 12-bar monthly mini chart whose
 * footer line swaps to a per-month sentence on hover.
 *
 * Bars are zero-based (see barHeightPct): a month with a genuine zero has to read
 * as zero, which matters most for "Überfällige Fälle" where zero is the goal.
 * A month with no data renders no bar at all over a faint baseline, so "nothing
 * measured" and "measured zero" stay visually distinct.
 */
export default function TrendCard({ trend }) {
  const [hovered, setHovered] = useState(null);
  const { attention } = colorsSemantic;

  const months = trend.months ?? [];
  const max = seriesMax(months);
  const lastIndex = months.length - 1;

  const deltaText = formatDeltaPct(trend.delta_pct);
  const deltaTone =
    trend.improving == null
      ? attention.delta.neutral
      : trend.improving
        ? attention.delta.good
        : attention.delta.bad;

  const hoveredMonth = hovered != null ? months[hovered] : null;

  // The headline is the newest month that measured something, which for a
  // duration metric is often not the current one. Say which month it is, so a
  // figure that looks stale next to a fresh chart is explained rather than
  // guessed at.
  const headlineMonth = months.find((month) => month.ym === trend.value_ym);
  const headlineIsCurrent = !headlineMonth || headlineMonth.ym === months[lastIndex]?.ym;
  const defaultHint =
    headlineMonth && !headlineIsCurrent
      ? `Letzte 12 Monate · Stand ${headlineMonth.label} ${headlineMonth.year}`
      : DEFAULT_HINT;

  const hint =
    hoveredMonth?.note ??
    (hoveredMonth && hoveredMonth.value == null
      ? `${hoveredMonth.label} ${hoveredMonth.year}: keine Daten`
      : null) ??
    defaultHint;

  return (
    <Card variant="surface" sx={{ padding: "15px 17px", height: "100%" }}>
      {/* Title + headline value on the left, delta pill top-right. */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "10px" }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            title={trend.label}
            sx={{
              fontSize: "13.5px",
              fontWeight: 700,
              color: colorsSemantic.text.strong,
              letterSpacing: "-.1px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {trend.label}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: "5px", mt: "5px" }}>
            <Typography
              component="span"
              sx={{ fontSize: "24px", fontWeight: 700, letterSpacing: "-.6px", lineHeight: 1 }}
            >
              {formatDe(trend.value, trend.decimals ?? 0)}
            </Typography>
            <Typography component="span" sx={{ fontSize: "12px", color: colorsSemantic.text.muted }}>
              {trend.unit}
            </Typography>
          </Box>
        </Box>

        {/* Rendered even without a delta so card headers stay the same height. */}
        <Box
          component="span"
          sx={{
            flex: "none",
            fontSize: "11px",
            fontWeight: 700,
            borderRadius: "20px",
            padding: "3px 9px",
            whiteSpace: "nowrap",
            minHeight: "17px",
            backgroundColor: deltaText ? deltaTone.bg : "transparent",
            color: deltaTone.fg,
          }}
        >
          {deltaText ?? ""}
        </Box>
      </Box>

      {/* Bars */}
      <Box
        role="img"
        aria-label={`${trend.label}: Verlauf der letzten ${months.length} Monate in ${trend.unit}`}
        sx={{ display: "flex", alignItems: "flex-end", gap: "4px", height: `${BAR_AREA_HEIGHT}px`, mt: "13px" }}
      >
        {months.map((month, i) => {
          const pct = barHeightPct(month.value, max);
          const isCurrent = i === lastIndex;
          const isHovered = i === hovered;
          const label = `${month.label} ${month.year}${month.partial ? " (laufend)" : ""}`;

          return (
            <Tooltip
              key={month.ym}
              arrow
              placement="top"
              title={
                month.value == null
                  ? `${label}: keine Daten`
                  : `${label}: ${formatDe(month.value, trend.decimals ?? 0)} ${trend.unit}`
              }
            >
              <Box
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: "flex",
                  alignItems: "flex-end",
                  height: "100%",
                  cursor: "default",
                  // Year boundary: a hairline in the gap rather than a wider
                  // label, which would cost horizontal room we do not have.
                  borderLeft:
                    i > 0 && month.year !== months[i - 1].year
                      ? `1px solid ${colorsSemantic.border.card}`
                      : "none",
                }}
              >
                {pct == null ? (
                  // No data: just the baseline, so it cannot be read as zero.
                  <Box sx={{ width: "100%", height: "1px", backgroundColor: attention.bar.baseline }} />
                ) : (
                  <Box
                    sx={{
                      width: "100%",
                      height: `${pct}%`,
                      // A measured zero still shows a mark, distinct from no data.
                      minHeight: pct === 0 ? "2px" : "3px",
                      borderRadius: "4px 4px 0 0",
                      transition: "background-color .15s ease",
                      backgroundColor: isHovered
                        ? attention.bar.hover
                        : isCurrent
                          ? attention.bar.current
                          : attention.bar.recessive,
                    }}
                  />
                )}
              </Box>
            </Tooltip>
          );
        })}
      </Box>

      {/* Month labels — every slot renders, only the text is conditional. */}
      <Box sx={{ display: "flex", gap: "4px", mt: "6px" }}>
        {months.map((month, i) => {
          const visible = monthLabelVisible(i, months.length, hovered);
          return (
            <Typography
              key={month.ym}
              component="span"
              sx={{
                flex: 1,
                minWidth: 0,
                textAlign: "center",
                fontSize: "9.5px",
                fontWeight: i === hovered ? 700 : 500,
                color: i === hovered ? colorsSemantic.text.strong : colorsSemantic.border.control,
                visibility: visible ? "visible" : "hidden",
              }}
            >
              {month.label}
            </Typography>
          );
        })}
      </Box>

      <Typography sx={{ fontSize: "11.5px", color: colorsSemantic.text.muted, mt: "9px", minHeight: "16px" }}>
        {hint}
      </Typography>
    </Card>
  );
}

TrendCard.propTypes = {
  trend: PropTypes.shape({
    key: PropTypes.string,
    label: PropTypes.string.isRequired,
    unit: PropTypes.string,
    decimals: PropTypes.number,
    value: PropTypes.number,
    value_ym: PropTypes.string,
    delta_pct: PropTypes.number,
    improving: PropTypes.bool,
    months: PropTypes.arrayOf(
      PropTypes.shape({
        ym: PropTypes.string,
        label: PropTypes.string,
        year: PropTypes.number,
        value: PropTypes.number,
        n: PropTypes.number,
        partial: PropTypes.bool,
        note: PropTypes.string,
      })
    ),
  }).isRequired,
};
